import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useHospitalOnboarding } from '../../../contexts/HospitalOnboardingContext';
import {
  Truck,
  Building2,
  FileText,
  User,
  Mail,
  Phone,
  MapPin,
  Mic,
  ArrowRight,
  Bookmark,
  Check
} from 'lucide-react';

const CATEGORIES = [
  'Private Operator',
  'Hospital Dedicated Vendor',
  'Trust / NGO Provider',
  'Individual Fleet Owner'
];

const SPECIALIZATIONS = [
  'Emergency ALS/BLS',
  'Patient Transport (Non-Emergency)',
  'Intercity Medical Transit',
  'Neonatal / Pediatric ICU',
  'Events & Industrial Standby'
];

export const Step1FleetOwnerProfile = () => {
  const navigate = useNavigate();
  const { formData, updateFields, saveAndContinue, saveAndExit, isSaving } = useHospitalOnboarding();
  const [errors, setErrors] = useState({});

  const handleSpecializationToggle = (item) => {
    const current = formData.fleet_specialization || [];
    const updated = current.includes(item)
      ? current.filter((x) => x !== item)
      : [...current, item];
    updateFields({ fleet_specialization: updated });
  };

  const handleContinue = (e) => {
    e.preventDefault();
    setErrors({});

    const newErrors = {};
    const phoneVal = (formData.admin_phone || formData.mobile || '').trim();
    if (!phoneVal) {
      newErrors.phone = 'Mobile number is required';
    } else if (!/^\d{10}$/.test(phoneVal)) {
      newErrors.phone = 'Mobile number must be exactly 10 digits';
    }

    const waVal = (formData.whatsapp_number || '').trim();
    if (waVal && !/^\d{10}$/.test(waVal)) {
      newErrors.whatsapp_number = 'WhatsApp number must be exactly 10 digits';
    }

    const emContact = (formData.emergency_contact || '').trim();
    if (emContact && !/^\d{10}$/.test(emContact)) {
      newErrors.emergency_contact = 'Emergency contact must be exactly 10 digits';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    saveAndContinue();
  };

  return (
    <div className="w-full">
      {/* Header Row: Step info and Switch to Voice */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
        <div>
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600 block mb-1">
            STEP 1 OF 6
          </span>
          <h2 className="text-2xl sm:text-3xl font-semibold text-slate-800 tracking-tight leading-tight">
            Fleet owner profile
          </h2>
          <p className="text-xs sm:text-sm text-slate-500 mt-1">
            Basic fleet operator identification, registration & contact details.
          </p>
          {/* Smart Ambulance Blue accent line */}
          <div className="w-14 sm:w-16 h-0.5 bg-blue-600 mt-2 rounded-full" />
        </div>

        <button
          type="button"
          onClick={() => navigate('/hospital/onboarding/voice?type=fleet&step=1')}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-slate-200 bg-white hover:bg-slate-50 text-slate-700 text-xs font-medium shadow-2xs transition-colors self-start cursor-pointer"
        >
          <Mic className="w-3.5 h-3.5 text-slate-600" />
          <span>Switch to voice</span>
        </button>
      </div>

      <form onSubmit={handleContinue} className="space-y-6">
        {/* Card 1: Fleet Operator Details */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="flex items-center gap-2.5 pb-4 mb-5 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <Truck className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Fleet operator details</h3>
              <p className="text-xs text-slate-400">Identification and operational classification</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Fleet Operator Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter fleet operator name"
                value={formData.operator_name || ''}
                onChange={(e) => updateFields({ operator_name: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Business License / Registration No.
              </label>
              <input
                type="text"
                placeholder="Enter business license or registration number"
                value={formData.business_license || ''}
                onChange={(e) => updateFields({ business_license: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-5">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                GSTIN
              </label>
              <input
                type="text"
                maxLength={15}
                placeholder="e.g. 24AAAAA0000A1Z5"
                value={formData.gstin || ''}
                onChange={(e) => updateFields({ gstin: e.target.value.toUpperCase() })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 uppercase font-mono transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                PAN
              </label>
              <input
                type="text"
                maxLength={10}
                placeholder="e.g. ABCDE1234F"
                value={formData.pan || ''}
                onChange={(e) => updateFields({ pan: e.target.value.toUpperCase() })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 uppercase font-mono transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Years in Ambulance Business
              </label>
              <input
                type="number"
                min="0"
                max="100"
                placeholder="e.g. 5"
                value={formData.years_in_business ?? formData.years_in_ambulance_business ?? ''}
                onChange={(e) => updateFields({ years_in_business: e.target.value, years_in_ambulance_business: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>
          </div>

          <div className="mb-5">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Operator Category
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {CATEGORIES.map((cat) => {
                const isSelected = formData.category === cat;
                return (
                  <button
                    key={cat}
                    type="button"
                    onClick={() => updateFields({ category: cat })}
                    className={`px-3 py-2.5 rounded-xl border text-xs font-medium transition-all text-left flex items-center justify-between ${
                      isSelected
                        ? 'border-blue-600 bg-blue-50 text-blue-700 shadow-2xs'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <span>{cat}</span>
                    {isSelected && <Check className="w-3.5 h-3.5 text-blue-600" />}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Fleet Specialization
            </label>
            <div className="flex flex-wrap gap-2">
              {SPECIALIZATIONS.map((spec) => {
                const isChecked = (formData.fleet_specialization || []).includes(spec);
                return (
                  <button
                    key={spec}
                    type="button"
                    onClick={() => handleSpecializationToggle(spec)}
                    className={`px-3 py-1.5 rounded-full border text-xs font-medium transition-all flex items-center gap-1.5 ${
                      isChecked
                        ? 'border-blue-600 bg-blue-50 text-blue-700'
                        : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'
                    }`}
                  >
                    <div
                      className={`w-3.5 h-3.5 rounded-sm flex items-center justify-center border ${
                        isChecked
                          ? 'bg-blue-600 border-blue-600 text-white'
                          : 'border-slate-300 bg-white'
                      }`}
                    >
                      {isChecked && <Check className="w-2.5 h-2.5 stroke-[3]" />}
                    </div>
                    <span>{spec}</span>
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Card 2: Official Contact & Base Location */}
        <div className="bg-white rounded-2xl border border-slate-200 p-6 sm:p-7 shadow-xs">
          <div className="flex items-center gap-2.5 pb-4 mb-5 border-b border-slate-100">
            <div className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <User className="w-4 h-4 text-blue-600" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-slate-800">Official contact & location</h3>
              <p className="text-xs text-slate-400">Owner, admin, and primary fleet dispatch base</p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Admin / Owner Name <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                placeholder="Enter admin / owner name"
                value={formData.admin_name || ''}
                onChange={(e) => updateFields({ admin_name: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Official Email
              </label>
              <input
                type="email"
                placeholder="Enter official email"
                value={formData.admin_email || formData.email || ''}
                onChange={(e) => updateFields({ admin_email: e.target.value, email: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Mobile Number <span className="text-red-500">*</span>
              </label>
              <input
                type="tel"
                maxLength={10}
                placeholder="Enter 10-digit mobile number"
                value={formData.admin_phone || formData.mobile || ''}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/\D/g, '').slice(0, 10);
                  const updates = { admin_phone: sanitized, mobile: sanitized };
                  if (formData.is_whatsapp_same) {
                    updates.whatsapp_number = sanitized;
                  }
                  updateFields(updates);
                  if (errors.phone) setErrors((prev) => ({ ...prev, phone: '' }));
                }}
                className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none transition-all ${
                  errors.phone
                    ? 'border-red-400 bg-red-50/20 focus:ring-1 focus:ring-red-500 focus:border-red-500'
                    : 'border-slate-200 focus:ring-1 focus:ring-blue-600 focus:border-blue-600'
                }`}
              />
              {errors.phone && (
                <p className="text-[11px] text-red-600 mt-1 font-medium">{errors.phone}</p>
              )}
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-medium text-slate-600">
                  WhatsApp Number
                </label>
                <label className="inline-flex items-center gap-1.5 text-[11px] text-blue-600 font-medium cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={!!formData.is_whatsapp_same}
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      const currentPhone = formData.admin_phone || formData.mobile || '';
                      updateFields({
                        is_whatsapp_same: isChecked,
                        whatsapp_number: isChecked ? currentPhone : (formData.whatsapp_number || '')
                      });
                      if (errors.whatsapp_number) setErrors((prev) => ({ ...prev, whatsapp_number: '' }));
                    }}
                    className="w-3.5 h-3.5 text-blue-600 rounded border-slate-300 focus:ring-blue-500 cursor-pointer"
                  />
                  <span>Same as mobile number</span>
                </label>
              </div>
              <input
                type="tel"
                maxLength={10}
                placeholder="Enter 10-digit WhatsApp number"
                value={formData.whatsapp_number || ''}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/\D/g, '').slice(0, 10);
                  const currentPhone = formData.admin_phone || formData.mobile || '';
                  updateFields({
                    whatsapp_number: sanitized,
                    is_whatsapp_same: sanitized === currentPhone && sanitized.length > 0
                  });
                  if (errors.whatsapp_number) setErrors((prev) => ({ ...prev, whatsapp_number: '' }));
                }}
                className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none transition-all ${
                  errors.whatsapp_number
                    ? 'border-red-400 bg-red-50/20 focus:ring-1 focus:ring-red-500 focus:border-red-500'
                    : 'border-slate-200 focus:ring-1 focus:ring-blue-600 focus:border-blue-600'
                }`}
              />
              {errors.whatsapp_number && (
                <p className="text-[11px] text-red-600 mt-1 font-medium">{errors.whatsapp_number}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">
                Emergency 24x7 Contact
              </label>
              <input
                type="tel"
                maxLength={10}
                placeholder="Enter emergency contact number"
                value={formData.emergency_contact || ''}
                onChange={(e) => {
                  const sanitized = e.target.value.replace(/\D/g, '').slice(0, 10);
                  updateFields({ emergency_contact: sanitized });
                  if (errors.emergency_contact) setErrors((prev) => ({ ...prev, emergency_contact: '' }));
                }}
                className={`w-full px-3.5 py-2.5 bg-white border rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none transition-all ${
                  errors.emergency_contact
                    ? 'border-red-400 bg-red-50/20 focus:ring-1 focus:ring-red-500 focus:border-red-500'
                    : 'border-slate-200 focus:ring-1 focus:ring-blue-600 focus:border-blue-600'
                }`}
              />
              {errors.emergency_contact && (
                <p className="text-[11px] text-red-600 mt-1 font-medium">{errors.emergency_contact}</p>
              )}
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Registered Address
            </label>
            <input
              type="text"
              placeholder="Enter official registered business address"
              value={formData.registered_address || ''}
              onChange={(e) => updateFields({ registered_address: e.target.value })}
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
            />
          </div>

          <div className="mb-4">
            <label className="block text-xs font-medium text-slate-600 mb-1">
              Base Address
            </label>
            <input
              type="text"
              placeholder="Enter complete base address"
              value={formData.address || formData.full_address || ''}
              onChange={(e) => updateFields({ address: e.target.value, full_address: e.target.value })}
              className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">City</label>
              <input
                type="text"
                placeholder="Enter city"
                value={formData.city || ''}
                onChange={(e) => updateFields({ city: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">State</label>
              <input
                type="text"
                placeholder="Enter state"
                value={formData.state || ''}
                onChange={(e) => updateFields({ state: e.target.value })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">PIN Code</label>
              <input
                type="text"
                maxLength={6}
                placeholder="Enter PIN code"
                value={formData.pincode || ''}
                onChange={(e) => updateFields({ pincode: e.target.value.replace(/\D/g, '') })}
                className="w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-xs sm:text-sm text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-blue-600 focus:border-blue-600 transition-all"
              />
            </div>
          </div>
        </div>

        {/* Bottom Form Action Buttons */}
        <div className="flex items-center justify-between pt-4 border-t border-slate-200">
          <button
            type="button"
            onClick={() => {
              if (typeof window !== 'undefined' && window.__ccOnboardingBack) {
                window.__ccOnboardingBack();
                return;
              }
              navigate('/hospital/register');
            }}
            className="px-5 py-2.5 rounded-xl border border-slate-200 hover:bg-slate-50 text-slate-600 text-xs sm:text-sm font-medium transition-colors cursor-pointer"
          >
            Back
          </button>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={saveAndExit}
              disabled={isSaving}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 rounded-xl border border-slate-300 hover:bg-slate-50 text-slate-700 text-xs sm:text-sm font-medium transition-colors cursor-pointer"
            >
              <Bookmark className="w-4 h-4 text-slate-500" />
              <span>{isSaving ? 'Saving...' : 'Save & exit'}</span>
            </button>

            <button
              type="submit"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs sm:text-sm font-medium shadow-md hover:shadow-lg transition-all cursor-pointer"
            >
              <span>Save & continue</span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default Step1FleetOwnerProfile;
